import { useCustomObjectWizard } from 'components/Wizards/CustomObject/CustomObjectWizardContext';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import CustomObjectsService from 'services/CustomObjectsService';
import AutomationsService from 'services/Automation2Service';
import SmartConnectorService from 'services/SmartConnectorService';
import { useAsync } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setError } from 'store/errors';
import { SMART_CONNECTOR_TYPES } from 'pages/SmartConnectors/constants';
import { StyledLoader } from '../../styles';
import { FORCE_ALL_RECORDS_SIZE } from 'services/helpers';
import { StyledActionsList, StyledAddActionButton } from './styles';
import Subsection from 'components/Wizards/CustomObject/components/Subsection';
import { CustomActionCard } from './CustomActionCard';
import {
  CUSTOM_AUTOMATION_ACTIONS,
  CUSTOM_SMART_CONNECTOR_ACTIONS,
} from './actions';
import { getErrors } from './useErrors';
import { toastVariant, useToast } from 'components/ToastProvider';
import { invalidate } from 'queries/invalidate';

const EMPTY_ARRAY = [];

const getPayload = (action) => {
  const { automation, ...rest } = action;
  return {
    ...rest,
    automation: automation?.id && { id: automation?.id },
  };
};

export const CustomActions = ({ model, setDirty }) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const dispatch = useDispatch();
  const teamMember = useSelector((s) => s.authentication.teamMember);
  const { preSaveFn: setPreSaveCallBack } = useCustomObjectWizard();

  const touchedActions = useRef({});
  const initialActionOverrideCreate = useRef(model.actionOverrideCreate);

  const [customActions, setCustomActions] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const presaveCallback = useCallback(async () => {
    setSaving(true);
    setErrors({});

    // handle CRUD on all touched actions we need to keep reference to all newly created actions
    // for state to be updated properly so all resolved POST and PATCH results are [id, result];
    // all resolved DELETE results are true;
    const results = await Promise.all(
      Object.entries(touchedActions.current).map(([id, action]) => {
        const payload = action && getPayload(action);

        if (model.name === 'client_client') {
          invalidate.CLIENTS.DEFAULT_MODEL();
        }

        return new Promise((resolve) => {
          if (payload === null) {
            if (id.startsWith('new_')) {
              delete touchedActions.current[id];
              resolve(true);
            } else {
              CustomObjectsService.deleteCustomAction(model.id, id)
                .then(() => {
                  delete touchedActions.current[id];
                  resolve(true);
                })
                .catch(() => {
                  resolve(false);
                });
            }
          } else if (id.startsWith('new_')) {
            CustomObjectsService.createCutomAction(model.id, payload)
              .then((data) => {
                delete touchedActions.current[id];
                resolve([id, data]);
              })
              .catch((err) => {
                setErrors((prev) => ({ ...prev, [id]: getErrors(err) }));
                resolve(false);
              });
          } else {
            CustomObjectsService.updateCustomAction(model.id, id, payload)
              .then((data) => {
                delete touchedActions.current[id];
                resolve([id, data]);
              })
              .catch((err) => {
                setErrors((prev) => ({ ...prev, [id]: getErrors(err) }));
                resolve(false);
              });
          }
        });
      })
    );

    // update state with fulfilled results;
    const resultsById = results.reduce((acc, result, i) => {
      if (Array.isArray(result)) {
        const [id, data] = result;
        acc[id] = data;
      }
      return acc;
    }, {});

    setCustomActions((prev) =>
      prev.map((action) => resultsById[action.id] || action)
    );

    const hasErrors = results.some((result) => !result);
    if (hasErrors) {
      showToast({
        message: t('There was an error saving custom actions.'),
        variant: toastVariant.FAILURE,
      });
    }
    const actionOverrideChanged =
      model.actionOverrideCreate !== initialActionOverrideCreate.current;
    setSaving(false);

    if (hasErrors) {
      return null;
    } else {
      initialActionOverrideCreate.current = model.actionOverrideCreate;
    }

    if (actionOverrideChanged) {
      return true;
    }

    return false;
  }, [model.id, model.actionOverrideCreate, showToast, model.name, t]);

  const {
    value: { automations, smartConnectors } = {
      automations: EMPTY_ARRAY,
      smartConnectors: EMPTY_ARRAY,
    },
    loading: customActionsLoading,
  } = useAsync(async () => {
    try {
      const [
        { results: customActions },
        { results: automations },
        { results: smartConnectors },
      ] = await Promise.all([
        CustomObjectsService.getCustomActions(model.id, {
          params: {
            page: 1,
            page_size: FORCE_ALL_RECORDS_SIZE,
            ordering: 'order',
          },
        }),
        AutomationsService.getAutomationsTypeahead({
          ordering: 'name',
          page: 1,
          page_size: FORCE_ALL_RECORDS_SIZE,
          custom_object_id: model.id,
          active: true,
        }),
        SmartConnectorService.getSmartConnectors({
          connectorType: SMART_CONNECTOR_TYPES.bulkaction,
          customObject: model.id,
        }),
      ]);

      setCustomActions(customActions);
      touchedActions.current = {};

      return { automations, smartConnectors };
    } catch (err) {
      if (err.isAxiosError) {
        dispatch(setError({ code: err.response?.status }));
      }
    }
  }, []);

  useEffect(() => {
    setPreSaveCallBack(presaveCallback);
  }, [setPreSaveCallBack, presaveCallback]);

  const {
    automationOptions,
    actionOptions,
    actionOptionLookup,
    smartConnectorOptions,
  } = useMemo(() => {
    const automationActions = [
      {
        value: CUSTOM_AUTOMATION_ACTIONS.START,
        label: t('Start Agentic Workflow'),
      },
      {
        value: CUSTOM_AUTOMATION_ACTIONS.START_AND_RESUME,
        label: t('Start (Resume Paused) Agentic Workflow'),
      },
      {
        value: CUSTOM_AUTOMATION_ACTIONS.PAUSE,
        label: t('Pause Agentic Workflow'),
      },
      {
        value: CUSTOM_AUTOMATION_ACTIONS.CANCEL,
        label: t('Cancel Agentic Workflow'),
      },
    ];

    const smartConnectorActions = [
      {
        value: CUSTOM_SMART_CONNECTOR_ACTIONS.START,
        label: t('Start SmartConnector'),
      },
    ];

    const actionOptionLookup = automationActions
      .concat(smartConnectorActions)
      .reduce((acc, option) => {
        acc[option.value] = option;
        return acc;
      }, {});

    const actionOptions = [
      { label: t('Agentic Workflow Actions'), options: automationActions },
      { label: t('SmartConnector Actions'), options: smartConnectorActions },
    ];

    const automationOptions = automations.map(({ id, name }) => {
      return { value: id, label: name };
    });

    const smartConnectorOptions = smartConnectors.map((sc) => {
      return { value: sc.id, label: sc.name };
    });

    return {
      actionOptions,
      actionOptionLookup,
      automationOptions,
      smartConnectorOptions,
    };
  }, [automations, smartConnectors, t]);

  const handleAddAction = () => {
    const newAction = {
      id: `new_${Date.now()}`,
      name: '',
      automation: null,
      action: CUSTOM_AUTOMATION_ACTIONS.START,
      order: customActions.length,
      sharing_settings: {
        all_team_members: 0,
        team_members: teamMember?.id
          ? [{ id: teamMember.id, display_name: teamMember.display_name }]
          : [],
        roles: [],
      },
    };
    setCustomActions((prev) => [...prev, newAction]);

    touchedActions.current[newAction.id] = newAction;
  };

  const handleDeleteAction = (id) => {
    setCustomActions((prev) => prev.filter((action) => action.id !== id));
    touchedActions.current[id] = null;
  };

  const handleReorderAction = (index, offset) => {
    const nextIndex = index + offset;
    if (nextIndex < 0 || nextIndex >= customActions.length) {
      return;
    }

    setCustomActions((prev) => {
      const next = [...prev];
      const [currentAction] = next.splice(index, 1);
      next.splice(nextIndex, 0, currentAction);

      touchedActions.current[currentAction.id] = {
        ...currentAction,
        order: nextIndex,
      };
      touchedActions.current[next[index].id] = { ...next[index], order: index };

      return next;
    });
  };

  const handleChange = useCallback(
    (id, key, value) => {
      setCustomActions((prev) =>
        prev.map((action) => {
          if (action.id === id) {
            const newAction = { ...action, [key]: value };
            touchedActions.current[id] = newAction;
            return newAction;
          }
          return action;
        })
      );
    },
    [setCustomActions]
  );

  const firstActionWithErrorId = useMemo(() => {
    const orderByIds = customActions.reduce((acc, action, i) => {
      acc[action.id] = i;
      return acc;
    }, {});
    return Object.entries(errors)
      .filter(([, err]) => err && Object.values(err).some(Boolean))
      .sort(([a], [b]) => orderByIds[a] - orderByIds[b])[0]?.[0];
  }, [errors, customActions]);

  const isDirty = !!Object.values(touchedActions.current).length;

  useLayoutEffect(() => {
    setDirty(isDirty);
  }, [isDirty, setDirty]);

  return (
    <>
      <Subsection title={t('Custom Actions')}>
        <StyledLoader loading={customActionsLoading} paddingTop={24}>
          <StyledActionsList>
            {customActions.map((action, i) => (
              <CustomActionCard
                key={action.id + i}
                {...action}
                action={actionOptionLookup[action.action]}
                automationOptions={automationOptions}
                actionOptions={actionOptions}
                smartConnectorOptions={smartConnectorOptions}
                errors={errors[action.id]}
                setErrors={setErrors}
                onChange={handleChange}
                onDelete={handleDeleteAction}
                onReorder={handleReorderAction}
                index={i}
                isLastIndex={i === customActions.length - 1}
                isFirstError={firstActionWithErrorId === action.id}
              />
            ))}
          </StyledActionsList>
          <StyledAddActionButton
            variant="text"
            onClick={handleAddAction}
            disabled={saving}
          >
            {t('+ Add Custom Action')}
          </StyledAddActionButton>
        </StyledLoader>
      </Subsection>
    </>
  );
};
